import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTH_NAMES = {
  it: ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};

const WEEKDAYS = {
  it: ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'],
  en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
};

export const AvailabilityCalendar = ({ roomId }) => {
  const { language } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookedDates, setBookedDates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAvailability();
  }, [roomId, currentDate]);

  const fetchAvailability = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const response = await axios.get(
        `${API}/rooms/${roomId}/availability-calendar?year=${year}&month=${month}`
      );
      setBookedDates(response.data.booked_dates);
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const changeMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek === -1) startDayOfWeek = 6;
    
    const days = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      days.push({
        day,
        dateStr,
        isBooked: bookedDates.includes(dateStr),
        isPast: new Date(year, month, day) < new Date(new Date().setHours(0, 0, 0, 0))
      });
    }
    return days;
  };

  const days = getDaysInMonth();

  return (
    <div className="bg-white p-6 rounded-lg border border-[#E3E5DF]/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="availability-calendar">
      <div className="flex items-center justify-between mb-6">
        <Button
          data-testid="calendar-prev-month"
          variant="ghost"
          size="sm"
          onClick={() => changeMonth(-1)}
          className="text-[#8A9A86] hover:bg-[#F0F2EB]"
        >
          <ChevronLeft size={20} />
        </Button>
        <h3 
          className="text-xl font-light text-[#2C302E]"
          style={{fontFamily: 'Cormorant Garamond, serif'}}
        >
          {MONTH_NAMES[language][currentDate.getMonth()]} {currentDate.getFullYear()}
        </h3>
        <Button
          data-testid="calendar-next-month"
          variant="ghost"
          size="sm"
          onClick={() => changeMonth(1)}
          className="text-[#8A9A86] hover:bg-[#F0F2EB]"
        >
          <ChevronRight size={20} />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {WEEKDAYS[language].map((day) => (
          <div key={day} className="text-center text-xs font-medium text-[#2C302E]/60 py-2 uppercase tracking-wider">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((dayObj, idx) => {
          if (!dayObj) {
            return <div key={`empty-${idx}`} className="aspect-square" />;
          }

          const { day, isBooked, isPast } = dayObj;
          
          let bgClass = 'bg-[#F0F2EB] text-[#2C302E] hover:bg-[#E3E5DF]';
          if (isPast) {
            bgClass = 'bg-gray-100 text-gray-400 cursor-not-allowed';
          } else if (isBooked) {
            bgClass = 'bg-[#C1A58D] text-white cursor-not-allowed';
          }

          return (
            <div
              key={dayObj.dateStr}
              data-testid={`calendar-day-${dayObj.dateStr}`}
              className={`aspect-square flex items-center justify-center text-sm rounded-md transition-colors ${bgClass}`}
              title={isBooked ? (language === 'it' ? 'Non disponibile' : 'Unavailable') : (language === 'it' ? 'Disponibile' : 'Available')}
            >
              {day}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-4 mt-6 text-xs text-[#2C302E]/60">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-[#F0F2EB]" />
          <span>{language === 'it' ? 'Disponibile' : 'Available'}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-[#C1A58D]" />
          <span>{language === 'it' ? 'Occupato' : 'Booked'}</span>
        </div>
      </div>
    </div>
  );
};
